import React, { useState } from "react";
import { auth, db, googleProvider } from "./firebase";
import { createUserWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";

const RegisterScreen = ({ onRegisterSuccess, onGoToLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [lineToken, setLineToken] = useState("");
  const [testUserId, setTestUserId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingGoogleUser, setPendingGoogleUser] = useState(null);

  const saveLineConfigToFirestore = async (uid, token, userId) => {
    await setDoc(doc(db, "users", uid), {
      lineToken: token,
      testUserId: userId,
    });
  };

  // 處理 Email/密碼 註冊
  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      return setError("兩次輸入的密碼不一致");
    }

    if (password.length < 6) {
      return setError("密碼長度至少需要 6 個字元");
    }

    if (!lineToken.trim()) return setError("請輸入Channel access token");
    if (!testUserId.trim()) return setError("請輸入測試帳號 ID");

    setLoading(true);
    try {
      const userCred = await createUserWithEmailAndPassword(auth, email, password);
      await saveLineConfigToFirestore(userCred.user.uid, lineToken.trim(), testUserId.trim());
      onRegisterSuccess();
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        setError("此 Email 已被註冊");
      } else if (err.code === "auth/invalid-email") {
        setError("無效的 Email 格式");
      } else {
        setError("註冊失敗：" + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // 處理 Google 快速註冊/登入
  const handleGoogleRegister = async () => {
    setError("");
    try {
      const result = await signInWithPopup(auth, googleProvider);
      setPendingGoogleUser(result.user);
    } catch (err) {
      setError("Google 註冊失敗");
    }
  };

  // Google 完成後：填寫 LINE 設定（必填）
  const handleCompleteGoogleLineConfig = async (e) => {
    e.preventDefault();
    setError("");
    if (!lineToken.trim()) return setError("請輸入Channel access token");
    if (!testUserId.trim()) return setError("請輸入測試帳號 ID");
    setLoading(true);
    try {
      await saveLineConfigToFirestore(pendingGoogleUser.uid, lineToken.trim(), testUserId.trim());
      setPendingGoogleUser(null);
      onRegisterSuccess();
    } catch (err) {
      setError("儲存失敗：" + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 💡 自訂緊湊型輸入框與標籤樣式 (覆蓋外部 CSS 的預設高度)
  const compactInputStyle = {
    width: "100%",
    padding: "6px 10px",
    minHeight: "36px", // 強制縮小輸入框高度
    marginBottom: 0,   // 取消外部預設的 margin
    fontSize: "13px",
    borderRadius: "6px"
  };
  const labelStyle = { display: "block", marginBottom: 2, fontSize: 12, color: "#aaa" };

  // Google 登入後需完成 LINE 設定的介面
  if (pendingGoogleUser) {
    return (
      <div style={{ width: "100vw", height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", background: "var(--bg-main)", overflow: "hidden" }}>
        <div style={{ 
          width: 360, 
          padding: "24px 28px", 
          background: "var(--bg-panel)", 
          borderRadius: 12, 
          border: "1px solid var(--border-color)", 
          boxShadow: "0 10px 25px rgba(0,0,0,0.5)", 
          textAlign: "center",
          maxHeight: "85vh", // 💡 加入最大高度限制
          overflowY: "auto"  // 💡 讓內部可以產生滑動條
        }}>
          <h2 style={{ marginBottom: 6, color: "#fff", fontWeight: "600", fontSize: 20 }}>完成 LINE 設定</h2>
          <p style={{ marginBottom: 16, color: "#aaa", fontSize: 12 }}>請填寫您的 LINE 官方帳號資訊</p>
          <form onSubmit={handleCompleteGoogleLineConfig} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ textAlign: "left" }}>
              <label style={labelStyle}>Channel access token</label>
              <input type="password" value={lineToken} onChange={(e) => setLineToken(e.target.value)} placeholder="LINE Channel Access Token" required style={compactInputStyle} />
            </div>
            <div style={{ textAlign: "left" }}>
              <label style={labelStyle}>測試帳號 ID</label>
              <input type="text" value={testUserId} onChange={(e) => setTestUserId(e.target.value)} placeholder="Target User ID" required style={compactInputStyle} />
            </div>
            {error && <div style={{ color: "#ef4444", fontSize: 12 }}>{error}</div>}
            <button type="submit" disabled={loading} style={{ marginTop: 6, padding: "10px", background: "var(--accent-color)", color: "#fff", border: "none", borderRadius: 6, fontSize: 14, fontWeight: "600", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
              {loading ? "儲存中..." : "完成設定"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // 主要註冊介面
  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "var(--bg-main)",
        overflow: "hidden" // 外層確保無側邊滑動條
      }}
    >
      <div
        style={{
          width: 360,
          padding: "24px 28px",
          background: "var(--bg-panel)",
          borderRadius: 12,
          border: "1px solid var(--border-color)",
          boxShadow: "0 10px 25px rgba(0,0,0,0.5)",
          textAlign: "center",
          maxHeight: "85vh", // 💡 限制卡片最大高度為螢幕高度的 85%
          overflowY: "auto"  // 💡 超出高度時，自動在卡片內部出現垂直滑動條
        }}
      >
        <h2 style={{ marginBottom: 4, color: "#fff", fontWeight: "600", fontSize: 22 }}>建立帳號</h2>
        <p style={{ marginBottom: 16, color: "#aaa", fontSize: 12 }}>
          加入我們，開始您的創作之旅
        </p>

        <form onSubmit={handleRegister} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ textAlign: "left" }}>
            <label style={labelStyle}>Email 帳號</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@mail.com"
              required
              style={compactInputStyle}
            />
          </div>

          <div style={{ textAlign: "left" }}>
            <label style={labelStyle}>設定密碼</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="至少 6 個字元"
              required
              style={compactInputStyle}
            />
          </div>

          <div style={{ textAlign: "left" }}>
            <label style={labelStyle}>確認密碼</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="再次輸入密碼"
              required
              style={compactInputStyle}
            />
          </div>

          <div style={{ textAlign: "left" }}>
            <label style={labelStyle}>
              Channel access token <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="password"
              value={lineToken}
              onChange={(e) => setLineToken(e.target.value)}
              placeholder="LINE Channel Access Token"
              required
              style={compactInputStyle}
            />
          </div>

          <div style={{ textAlign: "left" }}>
            <label style={labelStyle}>
              測試帳號 ID <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="text"
              value={testUserId}
              onChange={(e) => setTestUserId(e.target.value)}
              placeholder="Target User ID（用於 Push 測試）"
              required
              style={compactInputStyle}
            />
          </div>

          {error && <div style={{ color: "#ef4444", fontSize: 12 }}>{error}</div>}

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: 4,
              padding: "10px",
              background: "var(--accent-color)",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: "600",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "註冊中..." : "立即註冊"}
          </button>
        </form>

        <div style={{ margin: "12px 0", display: "flex", alignItems: "center", color: "#666", fontSize: 12 }}>
          <div style={{ flex: 1, height: "1px", background: "#333" }}></div>
          <span style={{ margin: "0 10px" }}>或</span>
          <div style={{ flex: 1, height: "1px", background: "#333" }}></div>
        </div>

        <button
          onClick={handleGoogleRegister}
          style={{
            width: "100%",
            padding: "8px",
            background: "#fff",
            color: "#444",
            border: "none",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" width="16" alt="G" />
          使用 Google 快速註冊
        </button>

        <div style={{ marginTop: 16, fontSize: 12, color: "#aaa" }}>
          已經有帳號了？{" "}
          <span
            onClick={onGoToLogin}
            style={{ color: "var(--accent-color)", cursor: "pointer", fontWeight: "600" }}
          >
            返回登入
          </span>
        </div>
      </div>
    </div>
  );
};

export default RegisterScreen;